import { Box, Typography, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import SidebarItem from './SidebarItemType';
import { useRouter } from 'next/router';
import Image from 'next/image';

const SIDEBAR_BG = '#F0EEE7';
const BRAND_GREEN = '#325127';
const SELECTED_BG = 'rgba(172,184,144,0.4)';

export interface SidebarProps {
  items: SidebarItem[];
  sidebarWidth: number;
  onExpandClick: () => void;
}

export default function Sidebar({ items, sidebarWidth = 240 }: SidebarProps) {
  const router = useRouter();
  const [selectedTab, setSelectedTab] = useState<string>(router.asPath);
  const [activeParents, setActiveParents] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedTab(router.asPath);
  }, [router.asPath]);

  const isSubpath = (mainPath: string, subPath: string) => {
    const normalizePath = (path: string) => (path === '/' ? path : path.replace(/\/+$/, ''));
    mainPath = normalizePath(mainPath);
    subPath = normalizePath(subPath);
    if (subPath === '/') return mainPath === '/';
    return mainPath === subPath || mainPath.startsWith(subPath + '/');
  };

  const handleClick = (path: string) => {
    if (path) {
      const normalizedPath = path.startsWith('/') ? path : '/' + path;
      router.push(normalizedPath);
    }
  };

  const toggleParent = (path: string) => {
    if (activeParents.has(path)) {
      setActiveParents(new Set());
    } else {
      setActiveParents(new Set([path]));
    }
  };

  const renderedItems = items
    .filter(({ isDisabled }) => !isDisabled)
    .map(({ name, path, childItems }, index, filteredItems) => {
      const isParentActive = isSubpath(selectedTab, path);
      const isChildActive = (childPath: string) => selectedTab === childPath;

      // Check if we need a divider before this item (between groups)
      const prevItem = index > 0 ? filteredItems[index - 1] : null;
      const hasDivider =
        prevItem?.childItems && prevItem.childItems.length > 0 && (!childItems || childItems.length === 0);

      return (
        <React.Fragment key={name + '-tab'}>
          {hasDivider && (
            <Box sx={{ px: 2, py: 1 }}>
              <Divider sx={{ borderColor: '#D3D3D3' }} />
            </Box>
          )}

          {/* Parent item */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: 2,
              py: '3px',
              bgcolor: SIDEBAR_BG,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: 210,
                px: 2,
                py: '4px',
                borderRadius: '5px',
                cursor: 'pointer',
                bgcolor: isParentActive && (!childItems || childItems.length === 0) ? SELECTED_BG : 'transparent',
                '&:hover': {
                  bgcolor:
                    isParentActive && (!childItems || childItems.length === 0) ? SELECTED_BG : 'rgba(172,184,144,0.2)',
                },
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (!childItems || childItems.length === 0) {
                  handleClick(path);
                } else {
                  toggleParent(path);
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  lineHeight: 1.5,
                  fontWeight: isParentActive ? 700 : 400,
                  color: BRAND_GREEN,
                }}
              >
                {name}
              </Typography>
            </Box>
          </Box>

          {/* Child items */}
          {childItems &&
            activeParents.has(path) &&
            childItems.map(({ path: childPath, name: childName, badge }) => (
              <Box
                key={childName + '-subtab'}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  px: 2,
                  py: '3px',
                  bgcolor: SIDEBAR_BG,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 210,
                    px: 4,
                    py: '4px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    bgcolor: isChildActive(childPath) ? SELECTED_BG : 'transparent',
                    '&:hover': {
                      bgcolor: isChildActive(childPath) ? SELECTED_BG : 'rgba(172,184,144,0.2)',
                    },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClick(childPath);
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '18px',
                      lineHeight: 1.5,
                      fontWeight: isChildActive(childPath) ? 700 : 400,
                      color: BRAND_GREEN,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {childName}
                  </Typography>
                  {badge != null && badge > 0 && (
                    <Box
                      sx={{
                        ml: '6px',
                        minWidth: '22px',
                        height: '20px',
                        px: '5px',
                        borderRadius: '10px',
                        backgroundColor: '#D8FF7F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#333',
                          lineHeight: 1,
                        }}
                      >
                        {badge}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
        </React.Fragment>
      );
    });

  return (
    <Box
      className="app-sidebar"
      sx={{
        width: sidebarWidth,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: sidebarWidth,
          height: '100vh',
          bgcolor: SIDEBAR_BG,
          boxShadow: '2px 4px 4px rgba(153, 153, 153, 0.25)',
        }}
      >
        {/* Logo area */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: '18px',
            height: 89,
            cursor: 'pointer',
            borderRadius: '0 20px 20px 0',
            bgcolor: SIDEBAR_BG,
          }}
          onClick={() => handleClick('/')}
        >
          <Image src="/icons/Motus_logo.svg" alt="Motus" width={116} height={48} />
        </Box>

        {/* Spacer */}
        <Box sx={{ height: 31 }} />

        {/* Navigation items */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {renderedItems}
        </Box>

        {/* Bottom spacer */}
        <Box sx={{ height: 24 }} />
      </Box>
    </Box>
  );
}
